/**
 * DOCX Handler - Preserves native Word formatting with round-trip editing
 *
 * Strategy:
 * 1. Convert DOCX → Native HTML (via Mammoth)
 * 2. Display with contentEditable (no custom CSS override)
 * 3. Parse edited HTML and rebuild as DOCX using docx library (client-side only)
 */

import mammoth from 'mammoth';
import {
  Document,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  Packer,
  BorderStyle,
  VerticalAlign,
  AlignmentType
} from 'docx';

/**
 * Extract native HTML from DOCX blob
 * DOCX files are ZIP archives - extract document.xml which contains the styled HTML
 * This preserves all Word formatting (colors, fonts, styles, tables, images, etc.)
 */
export async function extractHtmlFromDocx(arrayBuffer: ArrayBuffer): Promise<string> {
  try {
    // Check if the data looks like encrypted content (EA1 or EA2 magic bytes)
    const view = new Uint8Array(arrayBuffer.slice(0, 4));
    const magic = String.fromCharCode(...view.slice(0, 3));
    
    if (magic === 'EA1' || magic === 'EA2') {
      const errorMsg = `Received encrypted data (${magic}) instead of decrypted DOCX. Backend may not be decrypting properly.`;
      console.error('DOCX_EXTRACTION_ERROR:', {
        magic,
        firstBytes: Array.from(view.slice(0, 16)).map(b => b.toString(16).padStart(2, '0')).join(' '),
        bufferSize: arrayBuffer.byteLength,
        errorMsg
      });
      console.warn('Client-side decryption not available. The backend streaming service must decrypt before sending data.');
      throw new Error(errorMsg);
    }

    // Check if data looks like DOCX (should start with PK\x03\x04 - ZIP magic bytes)
    if (view[0] !== 0x50 || view[1] !== 0x4b) { // 'P', 'K'
      const errorMsg = `Invalid DOCX format. Expected ZIP magic bytes (PK), got: ${Array.from(view.slice(0, 4)).map(b => b.toString(16).padStart(2, '0')).join(' ')}`;
      console.error('DOCX_EXTRACTION_ERROR:', {
        expectedMagic: 'PK (0x50 0x4b)',
        actualMagic: Array.from(view.slice(0, 4)).map(b => b.toString(16).padStart(2, '0')).join(' '),
        bufferSize: arrayBuffer.byteLength,
        errorMsg
      });
      throw new Error(errorMsg);
    }

    // First, try to extract HTML directly from document.xml inside the DOCX (ZIP archive)
    // This is the "raw" Word HTML which includes all styling
    const { default: JSZip } = await import('jszip');
    const zip = new JSZip();
    await zip.loadAsync(arrayBuffer);

    // ── Track-changes pre-processing ──────────────────────────────
    // Mammoth ignores w:del / w:ins tracked-change markup:
    //   • w:del content (deleted text) is silently dropped
    //   • w:ins content (inserted text) is rendered as normal text
    // Mammoth also ignores w:color and w:u, so adding Word styles
    // isn't enough.  Instead we:
    //   1. Insert invisible Unicode PUA marker characters around
    //      tracked-change content in the XML
    //   2. Convert w:delText → w:t so mammoth can see the text
    //   3. Unwrap w:del / w:ins so mammoth treats runs as normal
    //   4. Feed modified DOCX to mammoth
    //   5. Post-process mammoth HTML: replace markers with <del>/<ins>
    //
    // PUA markers (Private Use Area – valid in XML, never in real text):
    //   U+E000 = DEL_OPEN   U+E001 = DEL_CLOSE
    //   U+E002 = INS_OPEN   U+E003 = INS_CLOSE

    const DEL_OPEN  = '\uE000';
    const DEL_CLOSE = '\uE001';
    const INS_OPEN  = '\uE002';
    const INS_CLOSE = '\uE003';

    const documentXml = zip.file('word/document.xml');
    if (documentXml) {
      const xmlContent = await documentXml.async('string');
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'application/xml');
      const W = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';

      let modified = false;

      // Helper: create a run containing a single marker character
      const createMarkerRun = (marker: string): Element => {
        const r = xmlDoc.createElementNS(W, 'w:r');
        const t = xmlDoc.createElementNS(W, 'w:t');
        t.textContent = marker;
        r.appendChild(t);
        return r;
      };

      // ── Convert w:del elements ──
      // Each <w:del> wraps <w:r> runs whose text lives in <w:delText>.
      // We convert w:delText → w:t, bracket with markers, and unwrap.
      const delElements = Array.from(xmlDoc.getElementsByTagNameNS(W, 'del'));
      for (const del of delElements) {
        const parent = del.parentNode;
        if (!parent) continue;

        // Opening marker
        parent.insertBefore(createMarkerRun(DEL_OPEN), del);

        // Move each run out of w:del, converting w:delText → w:t
        const runs = Array.from(del.getElementsByTagNameNS(W, 'r'));
        for (const run of runs) {
          const delTexts = Array.from(run.getElementsByTagNameNS(W, 'delText'));
          for (const dt of delTexts) {
            const newT = xmlDoc.createElementNS(W, 'w:t');
            newT.setAttribute('xml:space', 'preserve');
            newT.textContent = dt.textContent;
            dt.parentNode?.replaceChild(newT, dt);
          }
          parent.insertBefore(run, del);
        }

        // Closing marker + remove empty wrapper
        parent.insertBefore(createMarkerRun(DEL_CLOSE), del);
        parent.removeChild(del);
        modified = true;
      }

      // ── Convert w:ins elements ──
      // Each <w:ins> wraps <w:r> runs with the new text.
      // Bracket with markers and unwrap.
      const insElements = Array.from(xmlDoc.getElementsByTagNameNS(W, 'ins'));
      for (const ins of insElements) {
        const parent = ins.parentNode;
        if (!parent) continue;

        parent.insertBefore(createMarkerRun(INS_OPEN), ins);

        const runs = Array.from(ins.getElementsByTagNameNS(W, 'r'));
        for (const run of runs) {
          parent.insertBefore(run, ins);
        }

        parent.insertBefore(createMarkerRun(INS_CLOSE), ins);
        parent.removeChild(ins);
        modified = true;
      }

      // Write modified XML back into the ZIP
      if (modified) {
        const serializer = new XMLSerializer();
        const newXml = serializer.serializeToString(xmlDoc);
        zip.file('word/document.xml', newXml);
        arrayBuffer = await zip.generateAsync({ type: 'arraybuffer' });
      }
    }

    // Convert the (possibly modified) DOCX to HTML via Mammoth
    const result = await mammoth.convertToHtml({ arrayBuffer });
    let html = result.value;

    // ── Post-process: replace PUA markers with semantic HTML tags ──
    // The CSS in DocumentViewerModal already styles <del> (red strikethrough)
    // and <ins> (green underline), so we just need the correct tags.
    html = html
      .replace(/\uE000/g, '<del>')
      .replace(/\uE001/g, '</del>')
      .replace(/\uE002/g, '<ins>')
      .replace(/\uE003/g, '</ins>');

    return html;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    console.error('DOCX_EXTRACTION_FAILED:', {
      error: message,
      timestamp: new Date().toISOString()
    });
    throw new Error(`Failed to extract content from DOCX file: ${message}`);
  }
}

/**
 * Parse HTML element to extract text and styling
 */
function parseStyledText(element: HTMLElement): { text: string; bold?: boolean; italics?: boolean; underline?: boolean; color?: string }[] {
  const runs: { text: string; bold?: boolean; italics?: boolean; underline?: boolean; color?: string }[] = [];

  function walkNode(node: Node) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      if (text) {
        runs.push({ text });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tag = el.tagName.toLowerCase();
      const style = window.getComputedStyle(el);

      let isBold = tag === 'strong' || tag === 'b' || style.fontWeight === 'bold' || parseInt(style.fontWeight) >= 700;
      let isItalic = tag === 'em' || tag === 'i' || style.fontStyle === 'italic';
      let isUnderline = tag === 'u' || style.textDecoration.includes('underline');
      let color = style.color;

      // Normalize color to hex
      if (color && color !== 'rgba(0, 0, 0, 0)') {
        const rgb = color.match(/\d+/g);
        if (rgb && rgb.length >= 3) {
          color = '#' + [parseInt(rgb[0]), parseInt(rgb[1]), parseInt(rgb[2])]
            .map(x => x.toString(16).padStart(2, '0'))
            .join('')
            .toUpperCase();
        }
      }

      // Process children
      Array.from(node.childNodes).forEach(child => {
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent?.trim();
          if (text) {
            runs.push({
              text,
              bold: isBold,
              italics: isItalic,
              underline: isUnderline,
              color: color !== 'rgb(0, 0, 0)' ? color : undefined
            });
          }
        } else {
          walkNode(child);
        }
      });
    }
  }

  // Clean up runs - merge consecutive runs with same styling
  walkNode(element);

  // Merge consecutive runs with identical styling
  const merged: { text: string; bold?: boolean; italics?: boolean; underline?: boolean; color?: string }[] = [];
  for (const run of runs) {
    if (merged.length > 0) {
      const last = merged[merged.length - 1];
      if (
        last.bold === run.bold &&
        last.italics === run.italics &&
        last.underline === run.underline &&
        last.color === run.color
      ) {
        last.text += ' ' + run.text;
        continue;
      }
    }
    merged.push(run);
  }

  return merged.length > 0 ? merged : [{ text: element.textContent?.trim() || '' }];
}

/**
 * Convert styled text to TextRun objects
 */
function styledTextToRuns(styledText: { text: string; bold?: boolean; italics?: boolean; underline?: boolean; color?: string }[]): TextRun[] {
  return styledText.map(styled => new TextRun({
    text: styled.text,
    bold: styled.bold,
    italics: styled.italics,
    underline: styled.underline ? { type: 'single' } : undefined,
    color: styled.color?.replace('#', '')
  }));
}

/**
 * Parse paragraph element to extract text and apply styling
 */
function parseParagraphElement(element: HTMLElement): Paragraph {
  const style = window.getComputedStyle(element);
  const styledText = parseStyledText(element);
  const runs = styledTextToRuns(styledText);

  // Map text alignment to DOCX alignment type
  const alignmentMap: Record<string, string> = {
    'center': AlignmentType.CENTER,
    'right': AlignmentType.RIGHT,
    'justify': AlignmentType.JUSTIFIED
  };

  const alignment = (alignmentMap[style.textAlign] || AlignmentType.LEFT) as string;

  return new Paragraph({
    children: runs.length > 0 ? runs : [new TextRun(element.textContent?.trim() || '')],
    alignment: alignment as any
  });
}

/**
 * Parse table element and convert to DOCX Table
 */
function parseTableElement(tableEl: HTMLTableElement): Table {
  const rows: TableRow[] = [];

  Array.from(tableEl.querySelectorAll('tr')).forEach(trEl => {
    const cells: TableCell[] = [];

    Array.from(trEl.querySelectorAll('td, th')).forEach(tdEl => {
      const cellEl = tdEl as HTMLElement;
      const cellStyle = window.getComputedStyle(cellEl);

      const childParagraphs: Paragraph[] = [];
      Array.from(cellEl.childNodes).forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          const el = node as HTMLElement;
          const tag = el.tagName.toLowerCase();
          if (['p', 'div', 'span'].includes(tag)) {
            childParagraphs.push(parseParagraphElement(el));
          }
        } else if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent?.trim();
          if (text) {
            childParagraphs.push(new Paragraph(text));
          }
        }
      });

      cells.push(new TableCell({
        children: childParagraphs.length > 0 ? childParagraphs : [new Paragraph(cellEl.textContent?.trim() || '')],
        verticalAlign: VerticalAlign.CENTER,
        borders: {
          top: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          bottom: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          left: { style: BorderStyle.SINGLE, size: 1, color: '000000' },
          right: { style: BorderStyle.SINGLE, size: 1, color: '000000' }
        }
      }));
    });

    rows.push(new TableRow({
      children: cells
    }));
  });

  return new Table({
    rows,
    width: { size: 100, type: 'pct' }
  });
}

/**
 * Convert edited HTML back to DOCX
 * Parses HTML structure and rebuilds as DOCX with formatting preserved
 */
export async function htmlToDocx(
  htmlContent: string,
  fileName: string = 'document'
): Promise<Blob> {
  try {
    // Parse the edited HTML
    const parser = new DOMParser();
    const htmlDoc = parser.parseFromString(htmlContent, 'text/html');

    const docElements: (Paragraph | Table)[] = [];

    function processElement(element: Element) {
      const tag = element.tagName.toLowerCase();

      // Skip editor-specific elements
      if (tag === 'br' || tag === 'hr') return;

      // Handle different block types
      if (tag === 'p' || tag === 'div') {
        docElements.push(parseParagraphElement(element as HTMLElement));
      } else if (tag === 'h1' || tag === 'h2' || tag === 'h3' || tag === 'h4' || tag === 'h5' || tag === 'h6') {
        const para = parseParagraphElement(element as HTMLElement);
        docElements.push(para);
      } else if (tag === 'ul' || tag === 'ol') {
        // Parse list items
        Array.from(element.querySelectorAll('li')).forEach(li => {
          const styledText = parseStyledText(li as HTMLElement);
          const runs = styledTextToRuns(styledText);
          docElements.push(new Paragraph({
            children: runs.length > 0 ? runs : [new TextRun(li.textContent?.trim() || '')],
            bullet: { level: 0 } // Basic list support
          }));
        });
      } else if (tag === 'table') {
        docElements.push(parseTableElement(element as HTMLTableElement));
      } else {
        // Recursively process children
        Array.from(element.children).forEach(child => {
          processElement(child);
        });
      }
    }

    // Process all elements in body
    Array.from(htmlDoc.body.children).forEach(element => {
      processElement(element);
    });

    // Create DOCX document
    const docxDoc = new Document({
      sections: [{
        children: docElements.length > 0 ? docElements : [new Paragraph(htmlDoc.body.textContent?.trim() || 'Document')]
      }]
    });

    // Convert to blob
    const blob = await Packer.toBlob(docxDoc);
    return blob;
  } catch (error) {
    throw new Error('Failed to convert document to DOCX format');
  }
}

/**
 * Convert Blob to base64 for transmission
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 part after the data:application/...;base64, prefix
      const base64 = result.split(',')[1] || result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

/**
 * Convert base64 to Blob
 */
export function base64ToBlob(
  base64String: string,
  mimeType: string = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
): Blob {
  try {
    const byteCharacters = atob(base64String);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  } catch (error) {
    throw new Error('Failed to convert base64 to document');
  }
}
